import type { Gebaeudetyp } from '../planungsphase/types';

/**
 * Checklisten-Vorlagen des UfsA-Generators (Option B im Eintragsformular).
 *
 * Die Schluessel sind zugleich die Werte des Merkmals `gebaeudetyp` der
 * Planungsphase (`Gebaeudetyp` in `planungsphase/types.ts`) — so folgt die
 * Vorlage dem Steckbrief ohne Umschluesselung (Plan Stufe 3.4). `sonstiges`
 * hat bewusst keine Vorlage: eine Mischnutzung passt in keine der Listen.
 */
export interface UfsaVorlage {
    label: string;
    items: string[];
}

export const CHECKLIST_TEMPLATES: Record<string, UfsaVorlage> = {
    wohn: {
        label: 'Wohngebäude (MFH / EFH)',
        items: [
            'Steildach / Ziegeldeckung (Wartung)', 'Flachdach / Abdichtung', 'Dachflächenfenster', 'Kamin / Schornsteinfeger',
            'Regenrinnen / Fallrohre', 'Photovoltaik-Anlage (Aufdach)', 'Satellitenanlage', 'Schneefanggitter',
            'Fassadenanstrich / Putz', 'Fensterreinigung (Zugänglichkeit)', 'Rollläden / Sonnenschutz', 'Balkongeländer / Brüstung',
            'Heizungsanlage / Therme', 'Rauchwarnmelder (Wartung)', 'Außenwasserhahn', 'Garagentor (Antrieb)',
            'Mülltonnenstellplatz', 'Zuwege / Winterdienst', 'Hebeanlage (Abwasser)', 'Glasvordach Eingang',
        ],
    },
    buero: {
        label: 'Büro- & Verwaltungsbau',
        items: [
            'Glasfassade / Pfosten-Riegel', 'Fassadenbefahranlage (Gondel)', 'Automatiktüren / Drehtüren', 'Sonnenschutz (Raffstore/Markisen)',
            'Aufzugsanlagen (Personen)', 'Raumlufttechnik (RLT) / Lüftung', 'Klimaanlagen / Rückkühler (Dach)',
            'Brandmeldeanlage (BMZ)', 'Sprinkleranlage', 'Sicherheitsbeleuchtung / Notstrom', 'Serverraum / USV',
            'Teeküchen / Sanitärkerne', 'Doppelboden (Elektro)', 'Dachterrasse / Begrünung',
            'Tiefgarage / Schrankenanlage', 'Zutrittskontrollsysteme', 'Fluchtwegpläne / Beschilderung',
        ],
    },
    industrie: {
        label: 'Industriehalle / Gewerbebau',
        items: [
            'Lichtbänder / RWA (Dach)', 'Sekuranten / Seilsysteme (Dach)', 'Trapezblechfassade / Sandwich',
            'Hallenkräne / Hebezeuge', 'Sektionaltore / Rolltore', 'Laderampen / Überladebrücken',
            'Druckluftleitungen / Kompressor', 'Trafostation / Mittelspannung', 'Starkstromverteilung',
            'Gasdunkelstrahler / Deckenheizung', 'Regalanlagen (Hochregal)', 'Gabelstapler-Ladestation',
            'Gefahrstofflager / Auffangwannen', 'Ölabscheider / Koaleszenz', 'Zaunanlage / Schiebetor',
        ],
    },
    strasse: {
        label: 'Autobahn, Straßenbau',
        items: [
            'Fahrbahndecke (Asphalt/Beton)', 'Fahrbahnmarkierung', 'Schutzplanken / Leiteinrichtungen',
            'Schilderbrücken / Verkehrszeichen', 'Lärmschutzwände (Holz/Beton/Glas)', 'Entwässerungsmulden / Gräben',
            'Straßenabläufe / Kanalschächte', 'Beleuchtungsmasten', 'Notrufsäulen', 'Wildschutzzaun',
            'Böschungspflege / Gehölz', 'Sickerbecken / Regenrückhaltung', 'Ampelanlagen (LSA)',
            'Brückenbauwerke im Verlauf', 'Durchlässe',
        ],
    },
    bruecke: {
        label: 'Ingenieurbauwerk (Brücke)',
        items: [
            'Brückenlager / Gelenke', 'Fahrbahnübergangskonstruktion (ÜKO)', 'Kappen / Gehwege',
            'Geländer / Absturzsicherung', 'Berührungsschutz (Oberleitung)', 'Entwässerüngen (Längs/Quer)',
            'Vogelabwehrnetze / Gitter', 'Hohlkasten (Einstiege/Beleuchtung)', 'Widerlager / Kammerwände',
            'Besichtigungswagen / Schienen', 'Pfeiler / Stützen', 'Korrosionsschutz (Stahlteile)',
            'Betonoberflächen (Risse/Abplatzung)',
        ],
    },
};

/** Vorlage, wenn weder der Nutzer gewaehlt noch die Planungsphase etwas sagt. */
export const STANDARD_VORLAGE = 'wohn';

/**
 * Vorlagenschluessel aus dem Gebaeudetyp der Planungsphase. `null`, wenn der
 * Typ fehlt (ungeklaert) oder keine Vorlage hat (`sonstiges`) — dann gilt
 * weiter die bisherige Vorgabe. Die Wahl des Nutzers geht immer vor.
 */
export const vorlageAusGebaeudetyp = (typ: Gebaeudetyp | null | undefined): string | null =>
    typ && Object.prototype.hasOwnProperty.call(CHECKLIST_TEMPLATES, typ) ? typ : null;
